import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Facebook, Linkedin, Mail, Twitter } from 'lucide-react';

import Modal from '../../../shared/Modal/Modal';

const getSharePlatforms = (t) => ({
  linkedin: {
    icon: Linkedin,
    color: '#0077b5',
    hoverColor: '#006396',
    label: t('share.shareOnLinkedin'),
    getUrl: (url, text) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(
        text
      )}`,
  },
  twitter: {
    icon: Twitter,
    color: '#1DA1F2',
    hoverColor: '#1a91da',
    label: t('share.shareOnTwitter'),
    getUrl: (url, text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  facebook: {
    icon: Facebook,
    color: '#4267B2',
    hoverColor: '#385796',
    label: t('share.shareOnFacebook'),
    getUrl: (url, text) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
  },
  email: {
    icon: Mail,
    color: '#EA4335',
    hoverColor: '#D33828',
    label: t('share.shareViaEmail'),
  },
});

const ShareButton = ({ onClick, config }) => {
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-12 h-12 flex items-center justify-center rounded-full text-white transition-colors"
      style={{ backgroundColor: config.color, ':hover': { backgroundColor: config.hoverColor } }}
      aria-label={config.label}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
};

export const ShareModal = ({ isOpen, onClose, url, currentOrganization, showModal }) => {
  const { t } = useTranslation('common');
  const [shareError, setShareError] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const SHARE_PLATFORMS = getSharePlatforms(t);

  if (!isOpen) return null;

  const shareText = t('share.shareText', { organization: currentOrganization?.name });

  const handleCopyLink = async () => {
    setShareError(null);
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      setShareError(t('share.copyFailed'));
    }
  };

  const handleEmailShare = () => {
    const subject = t('share.emailSubject', { organization: currentOrganization?.name });
    const body = t('share.emailBody', { organization: currentOrganization?.name, url });

    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleShare = async (platform) => {
    // Reset any previous errors
    setShareError(null);

    if (platform === 'email') {
      handleEmailShare();
      return;
    }

    const config = SHARE_PLATFORMS[platform];
    const shareUrl = config.getUrl(url, shareText);

    // Try Web Share API first for mobile devices
    if (navigator.share && (platform === 'twitter' || platform === 'facebook')) {
      try {
        await navigator.share({
          title: `Join ${currentOrganization?.name}`,
          text: shareText,
          url,
        });
        return;
      } catch (error) {
        setShareError(t('share.sharingFailed'));
      }
    }

    // Fallback to popup
    const width = 600;
    const height = 400;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    window.open(
      shareUrl,
      'share',
      `width=${width},height=${height},left=${left},top=${top},location=0,menubar=0,toolbar=0,status=0,scrollbars=1`
    );
  };

  return (
    <Modal show={showModal} onHide={onClose} size="lg" centered>
      <Modal.CustomHeader closeButton>
        <Modal.Title>{t('share.shareCareerOpportunities')}</Modal.Title>
      </Modal.CustomHeader>

      <Modal.Body>
        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <h4 className="text-base font-semibold mb-2">
              {t('share.helpOthersJoin', { organization: currentOrganization?.name })}
            </h4>
            <p className="text-sm text-gray-600">{t('share.shareDescription')}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 justify-center mb-6">
            {Object.entries(SHARE_PLATFORMS).map(([platform, config]) => (
              <ShareButton key={platform} platform={platform} config={config} onClick={() => handleShare(platform)} />
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2">
            <input
              type="text"
              value={url}
              readOnly
              className="w-full flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
            />
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-full sm:w-auto px-4 py-2 bg-gray-100 border-none hover:bg-gray-200 text-gray-700 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {isCopied ? t('copied') : t('copy')}
            </button>
          </div>

          {shareError && <div className="px-4 sm:px-6 pb-3 text-sm text-red-600">{shareError}</div>}
        </div>
      </Modal.Body>
    </Modal>
  );
};
